/**
 * System design's side of the attribution rule (LG-930).
 *
 * A leaf, for the same reason `attribution.ts` is one: the homework display
 * lives in a tRPC router that imports Drizzle, while the SD admin editor and
 * problem table ask the same question from the browser. Neither may grow its
 * own copy of "which source represents this problem", and a client bundle must
 * not pull `db/` in to ask.
 *
 * This module only *flattens* SD rows into `AttributionCandidate`s. Every
 * ranking decision belongs to `attribution.ts`, so the two domains answer the
 * ordering question identically.
 */

import {
  type AttributionCandidate,
  findOwningCandidate,
  isCreditable,
  isOwningSourceId,
  OWNING_SOURCE_ID_PREFIX,
  resolveOriginalCandidate,
} from './attribution.js'

/**
 * The shape both callers can produce: one `SystemDesignProblemSource` row with
 * its `Source` already resolved through a `live()` join.
 *
 * `sourceSlug === null` means the join found nothing — the source was
 * soft-deleted — which is exactly what `isCreditable` refuses to credit.
 */
export interface SDSourceCandidateInput {
  /** The `SystemDesignProblemSource` row id. */
  id: string
  /** FK to `Source`. */
  sourceId: string
  sourceSlug: string | null
  /** Display name, already resolved; `null` when the source is gone. */
  sourceName: string | null
  sourceIcon: string | null
  /** This source's own title for the problem. */
  title: string | null
  /** Deep link to the problem on this source. */
  url: string | null
  /** The source's front page, when the caller has it. */
  siteUrl?: string | null
  premiumRequired: boolean
  isOriginal: boolean
}

/**
 * Flatten a problem's attribution rows, marking the owning one the way the
 * coding editor's `toCandidates` does.
 *
 * `SystemDesignProblem.sourceId` names a `Source`, not a
 * `SystemDesignProblemSource`, so the owning source is represented here by its
 * own attribution row: the row whose `sourceId` matches is *reused* — carrying
 * its title and deep link — and merely re-identified with the `owning:` prefix.
 * Emitting a second candidate for it would let one source be both credited and
 * listed as somewhere else to practise.
 *
 * The consequence: an owning source with no attribution row produces no owning
 * candidate at all, and `findOwningCandidate` finds nothing. Backfilled data
 * always has one (the owning source *is* the old order-0 row), and synthesizing
 * a candidate from the `Source` itself needs it resolved server-side for every
 * caller — that belongs with the source-picker follow-up.
 */
export function toSDCandidates(
  owningSourceId: string | null,
  rows: readonly SDSourceCandidateInput[],
): AttributionCandidate[] {
  return rows.map((row) => ({
    id:
      owningSourceId && row.sourceId === owningSourceId
        ? `${OWNING_SOURCE_ID_PREFIX}${row.id}`
        : row.id,
    sourceSlug: row.sourceSlug,
    name: row.sourceName ?? 'Unknown Source',
    icon: row.sourceIcon,
    title: row.title,
    url: row.url,
    siteUrl: row.siteUrl ?? null,
    premiumRequired: row.premiumRequired,
    isOriginal: row.isOriginal,
  }))
}

/**
 * The `SystemDesignProblemSource` row id a candidate came from.
 *
 * `toSDCandidates` re-identifies the owning row with an `owning:` prefix, so a
 * caller holding a chosen candidate cannot compare its `id` against its own
 * rows. Undoing that encoding belongs here, with the code that applies it —
 * callers that matched on list position instead were only correct while the
 * flattening stayed a strict 1:1 map, which nothing in the interface promises.
 */
export function sdCandidateRowId(candidate: AttributionCandidate): string {
  return isOwningSourceId(candidate.id)
    ? candidate.id.slice(OWNING_SOURCE_ID_PREFIX.length)
    : candidate.id
}

/**
 * The SD source shape the admin routers send to the browser.
 *
 * Declared structurally rather than imported from `RouterOutputs`: this module
 * is a leaf that `api/` imports, so it cannot depend on the router's types
 * without a cycle. TypeScript still checks the call sites against it.
 */
export interface SDRouterSourceRow {
  id: string
  sourceId: string
  /** Display name, already resolved; "Unknown" when the source is gone. */
  source: string
  sourceIcon?: string | null
  sourceSlug: string | null
  sourceSiteUrl?: string | null
  title: string
  url?: string | null
  premiumRequired: boolean
  isOriginal: boolean
}

/** {@link toSDCandidates} over the router's own field names. */
export function toSDCandidatesFromRouter(
  owningSourceId: string | null,
  rows: readonly SDRouterSourceRow[],
): AttributionCandidate[] {
  return toSDCandidates(
    owningSourceId,
    rows.map((row) => ({
      id: row.id,
      sourceId: row.sourceId,
      sourceSlug: row.sourceSlug,
      // `source` has already fallen back to the literal "Unknown" server-side,
      // so `sourceSlug` is what actually decides creditability here.
      sourceName: row.source,
      sourceIcon: row.sourceIcon ?? null,
      title: row.title,
      url: row.url ?? null,
      siteUrl: row.sourceSiteUrl ?? null,
      premiumRequired: row.premiumRequired,
      isOriginal: row.isOriginal,
    })),
  )
}

/**
 * Which row to name and link for a system design problem.
 *
 * The first two rungs are `resolveOriginalCandidate` verbatim — the marked row,
 * else the owning one — so SD and coding agree about who may be *credited*, and
 * neither can name BCTCI or a source whose row is gone.
 *
 * The third rung is where this parts company with coding, deliberately. A
 * homework card has to print something, and `resolveOriginalCandidate` returns
 * nothing for a problem BCTCI owns and marks nowhere — correct for a credit
 * line, wrong here, where it would render "Unknown" over a problem whose source
 * we know perfectly well. So an *uncredited* owning row is used as a fallback:
 * naming BCTCI as where to find the problem claims nothing about who wrote it.
 *
 * The last rungs keep a link on a problem that has neither marker. They prefer
 * a creditable source — somebody else's, and resolvable — over BCTCI, since a
 * problem also listed on LeetCode is more usefully pointed there than at
 * ourselves; BCTCI wins only when it is all there is. Both skip a source whose
 * row is gone, for the same reason the pill does: "Unknown Source" names
 * nobody.
 *
 * The uncredited-owning rung skips one too, which is why it is not a bare
 * `findOwningCandidate`. That rung exists for the source we know perfectly
 * well and merely may not credit — BCTCI — and a soft-deleted `Source` is the
 * opposite case: it renders the "Unknown" this rung was added to prevent, and
 * links a row nothing live points at, while a resolvable source may be sitting
 * one row down for the rungs below to find.
 */
export function pickSDDisplayCandidate(
  candidates: readonly AttributionCandidate[],
): AttributionCandidate | undefined {
  const owning = findOwningCandidate(candidates)
  return (
    resolveOriginalCandidate(candidates) ??
    (owning && owning.sourceSlug !== null ? owning : undefined) ??
    candidates.find(isCreditable) ??
    candidates.find((candidate) => candidate.sourceSlug !== null)
  )
}
